import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiClient } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Panel, PanelHeader, KPICard } from "@/components/ui-bits";
import { Button } from "@/components/ui/button";
import { Sparkles, AlertTriangle, Send } from "lucide-react";
import { toast } from "sonner";

export default function RetentionPage() {
  const { activeBranchId } = useAuth();
  const params = activeBranchId ? { branch_id: activeBranchId } : {};
  const [openInsight, setOpenInsight] = useState(null);
  const [insightText, setInsightText] = useState("");
  const [busyAI, setBusyAI] = useState(false);

  const { data: overview } = useQuery({
    queryKey: ["retention-overview", activeBranchId],
    queryFn: async () => (await apiClient.get("/retention/overview", { params })).data,
  });
  const { data: atRisk = [] } = useQuery({
    queryKey: ["retention-at-risk", activeBranchId],
    queryFn: async () => (await apiClient.get("/retention/at-risk", { params })).data,
  });

  const ov = overview || { buckets: { GREEN: 0, YELLOW: 0, RED: 0 }, statuses: {}, total_members: 0, avg_attendance: 0 };
  const total = ov.total_members || 1;
  const greenPct = (ov.buckets.GREEN / total) * 100;
  const yellowPct = (ov.buckets.YELLOW / total) * 100;
  const redPct = (ov.buckets.RED / total) * 100;

  const fetchInsight = async (member) => {
    setOpenInsight(member);
    setBusyAI(true);
    setInsightText("");
    try {
      const { data } = await apiClient.post("/retention/insight", { member_id: member.user_id });
      setInsightText(data.insight);
    } catch (e) {
      setInsightText("(AI service unavailable)");
    } finally {
      setBusyAI(false);
    }
  };

  return (
    <div className="p-8 space-y-6 animate-fade-in-up" data-testid="retention-page">
      <div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-volt font-semibold">Retention</div>
        <h1 className="font-display text-5xl font-black uppercase tracking-tight mt-1">Member Pulse</h1>
        <div className="text-zinc-500 text-sm mt-1">Traffic-light segmentation by 30-day attendance %.</div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard testId="kpi-active" label="Active Members" value={ov.statuses.ACTIVE || 0} sub={`${ov.total_members} total`} />
        <KPICard testId="kpi-avg-att" label="Avg Attendance" value={`${ov.avg_attendance}%`} sub="Rolling 30 days" />
        <KPICard testId="kpi-frozen" label="Frozen" value={ov.statuses.FROZEN || 0} accent="warning" sub="Paused memberships" />
        <KPICard testId="kpi-at-risk" label="At Risk" value={ov.statuses.AT_RISK || 0} accent="danger" sub="Need intervention" />
      </div>

      {/* Traffic light bar */}
      <Panel>
        <PanelHeader subtitle="Segmentation" title="Retention Traffic Light" />
        <div className="space-y-3">
          <SegRow label="Green · 80%+" count={ov.buckets.GREEN} pct={greenPct} color="#00E676" testId="seg-green" />
          <SegRow label="Yellow · 60-79%" count={ov.buckets.YELLOW} pct={yellowPct} color="#FFEA00" testId="seg-yellow" />
          <SegRow label="Red · below 60%" count={ov.buckets.RED} pct={redPct} color="#FF3B30" testId="seg-red" />
        </div>
      </Panel>

      {/* At risk list */}
      <Panel>
        <div className="flex items-center justify-between mb-4">
          <PanelHeader subtitle="Priority" title="At-Risk Members" />
        </div>
        <div className="space-y-2 max-h-[60vh] overflow-y-auto scrollbar-thin">
          {atRisk.slice(0, 40).map((m) => (
            <div key={m.id} data-testid={`risk-${m.user_id}`}
              className="flex items-center gap-3 p-3 bg-zinc-950 border border-zinc-900 rounded-sm hover:border-red-500/50 transition-colors">
              <div className="w-10 h-10 grid place-items-center bg-red-500/15 text-red-400 rounded-sm">
                <AlertTriangle size={16} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm">{m.name}</div>
                <div className="text-[10px] uppercase tracking-[0.16em] text-zinc-500">
                  Last visit: {m.last_visit ? new Date(m.last_visit).toLocaleDateString("en-IN") : "—"} · {m.phone}
                </div>
              </div>
              <div className="font-display text-2xl font-black text-red-400">{Math.round(m.attendance_pct)}%</div>
              <Button size="sm" variant="outline" onClick={() => fetchInsight(m)} data-testid={`insight-${m.user_id}`}
                className="bg-zinc-900 border-zinc-800 hover:border-volt text-volt">
                <Sparkles size={12} className="mr-1" /> AI Insight
              </Button>
            </div>
          ))}
          {atRisk.length === 0 && <div className="text-zinc-600 text-sm py-8 text-center">No at-risk members.</div>}
        </div>
      </Panel>

      {/* Inline AI panel */}
      {openInsight && (
        <Panel>
          <PanelHeader title={`AI Insight · ${openInsight.name}`} subtitle="Claude Sonnet 4.5"
            action={<Button onClick={() => setOpenInsight(null)} variant="outline" className="bg-zinc-900 border-zinc-800 text-zinc-300">Close</Button>}
          />
          {busyAI ? (
            <div className="text-zinc-500 text-sm py-6">Analyzing attendance patterns…</div>
          ) : (
            <div className="text-sm text-zinc-300 leading-relaxed whitespace-pre-wrap font-mono-cmd bg-zinc-950 border border-zinc-900 p-4 rounded-sm"
              data-testid="ai-insight-text">{insightText}</div>
          )}
        </Panel>
      )}
    </div>
  );
}

function SegRow({ label, count, pct, color, testId }) {
  return (
    <div data-testid={testId}>
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full" style={{ background: color, boxShadow: `0 0 8px ${color}` }} />
          <span className="text-xs uppercase tracking-[0.18em] font-semibold">{label}</span>
        </div>
        <div className="font-display text-2xl font-black" style={{ color }}>
          {count}<span className="text-sm text-zinc-500 ml-2">{Math.round(pct)}%</span>
        </div>
      </div>
      <div className="h-2 bg-zinc-900 rounded-sm overflow-hidden">
        <div className="h-full transition-all duration-500" style={{ width: `${Math.min(pct, 100)}%`, background: color }} />
      </div>
    </div>
  );
}
